/**
 * THE side a run's cabinets stand on — one answer for the whole run.
 *
 * Design 2026-11-03 (claude-docs/2026-11-03-angled-cabinet-runs-design.md):
 * a run has ONE side and keeps it round every bend — if the cabinets are on
 * your right as you walk the drawn line, they stay on your right on every leg.
 * The side is TRAVEL-relative (`sign` +1 = on `leftRot(travel)`, the engine's
 * inward-normal side), and it is decided once:
 *
 *   1. a wall-backed leg decides it — its cabinets face its wall's inward
 *      normal, so the room side is carried through every free leg;
 *   2. else the FIRST free leg's stored `cookSide`;
 *   3. else today's guess for that first leg (`defaultSidedness`: face the
 *      nearest other run).
 *
 * A stored `cookSide` keeps its SCREEN meaning — `'right'` is the inward normal
 * with `z >= 0` (`synthesizeVirtualWall`) — so the per-leg values this returns
 * are what the fill, the panel and the drag already read. A lone island leg
 * resolves exactly as before (`seg.cookSide ?? defaultSidedness(...)`).
 *
 * Every reader of a free leg's side goes through here (or reads the stamp the
 * fill leaves on its objects). Pass a HYDRATED run when it has wall legs.
 *
 * Pure. No I/O.
 */

const { defaultSidedness } = require('./freestanding');
const { isFreeLeg, legDirection, cabinetSideSign } = require('./bendGeometry');

const FLIP_REFUSAL = 'The wall decides which side these cabinets face.';

const leftRot = (d) => ({ x: -d.z, z: d.x });
const dot = (a, b) => a.x * b.x + a.z * b.z;

/** `leftRot` of the unit direction start → end, or null when degenerate. */
function leftNormal(start, end) {
  const dx = end.x - start.x;
  const dz = end.z - start.z;
  const len = Math.hypot(dx, dz);
  return len < 1e-12 ? null : { x: -dz / len, z: dx / len };
}

/**
 * The stored `cookSide` whose virtual wall faces `normal` — the inverse of
 * `synthesizeVirtualWall`, whose `'right'` is `leftRot(start→end)` flipped to
 * `z >= 0`.
 */
function cookSideForNormal(start, end, normal) {
  const r = leftNormal(start, end);
  if (!r) return 'right';
  const rightN = r.z >= 0 ? r : { x: -r.x, z: -r.z };
  return dot(normal, rightN) > 0 ? 'right' : 'left';
}

/** +1 when `cookSide` puts a free leg's cabinets on `leftRot(travel)`. */
function signOfCookSide(seg, cookSide) {
  const r = leftNormal(seg.start, seg.end);
  if (!r) return 1;
  const rightN = r.z >= 0 ? r : { x: -r.x, z: -r.z };
  const n = cookSide === 'left' ? { x: -rightN.x, z: -rightN.z } : rightN;
  return dot(n, r) > 0 ? 1 : -1;
}

const isStoredFree = (s) => s?.freestanding === true && s.start && s.end;

/**
 * The run's one side, travel-relative.
 *
 * @param {Object} run  hydrated run (stored is fine for an all-free island)
 * @param {{ otherCabinetRuns?: Array<Array<{x,z}>> }} [opts]  for the guess
 * @returns {{ sign: 1|-1, source: 'wall'|'stored'|'guess' }}
 */
function runCabinetSide(run, { otherCabinetRuns = [] } = {}) {
  const segs = run?.segments || [];
  const wallLeg = segs.find((s) => !isFreeLeg(s));
  if (wallLeg) {
    if (!wallLeg.wall) {
      throw new Error('runSide: a run with wall legs must be hydrated');
    }
    const dir = legDirection(wallLeg);
    // A wall leg's cabinets face leftRot(wallDir); its travel runs along
    // ±wallDir, so the sign is just which way the leg was drawn.
    const sign = dir
      ? Math.sign(wallLeg.endParam - wallLeg.startParam) || 1
      : 1;
    return { sign, source: 'wall' };
  }
  const free = segs.filter(isStoredFree);
  const first = free[0];
  if (!first) return { sign: 1, source: 'guess' };
  if (first.cookSide) {
    return { sign: signOfCookSide(first, first.cookSide), source: 'stored' };
  }
  const guess = defaultSidedness(first, free, otherCabinetRuns).cookSide;
  return { sign: signOfCookSide(first, guess), source: 'guess' };
}

/**
 * Wall legs (stored indices) whose cabinets stand on the OTHER side of the
 * drawn line from the run's side — a run that leaves one wall and reaches
 * another facing the other way. Each wall keeps its room side, so the
 * cabinets switch sides of the line there (design 2026-11-03: the first wall
 * leg decides the free legs; such a run is said, not guessed).
 *
 * @returns {number[]}
 */
function sideChangeLegs(run, opts = {}) {
  const { sign, source } = runCabinetSide(run, opts);
  const out = [];
  (run?.segments || []).forEach((seg, i) => {
    if (isFreeLeg(seg)) return;
    const own = cabinetSideSign(seg);
    if (own != null && own !== sign && source === 'wall') out.push(i);
  });
  return out;
}

/**
 * Every free leg's stored-convention `cookSide` for the run's one side, by
 * STORED segment index (`_storedIdx` on a hydrated leg).
 *
 * @returns {Map<number, 'left'|'right'>}
 */
function freeLegCookSides(run, opts = {}) {
  const { sign } = runCabinetSide(run, opts);
  const out = new Map();
  (run?.segments || []).forEach((seg, i) => {
    if (!isStoredFree(seg)) return;
    const r = leftNormal(seg.start, seg.end);
    if (!r) return;
    const normal = sign > 0 ? r : { x: -r.x, z: -r.z };
    out.set(seg._storedIdx ?? i, cookSideForNormal(seg.start, seg.end, normal));
  });
  return out;
}

/**
 * Why Flip A/B does not apply to this run, or null when it does — a run with
 * any wall leg takes its side from the wall. Read off the STORED run so the
 * panel can grey the button without hydrating anything; `flipRunSide` asks
 * the same question, so the button and the write cannot disagree.
 */
function flipRefusal(storedRun) {
  return (storedRun?.segments || []).some((s) => s && !s.freestanding)
    ? FLIP_REFUSAL
    : null;
}

/**
 * Flip A/B: turn a whole island round. Refused on a run with a wall leg —
 * the wall decides there (user, 2026-11-03), and the panel greys the button
 * with this reason rather than hiding it.
 *
 * @param {Object} storedRun   the run as stored (written back)
 * @param {Object} hydratedRun the same run hydrated (decides)
 * @returns {{ storedRun: Object } | { refused: string }}
 */
function flipRunSide(storedRun, hydratedRun, opts = {}) {
  const refused = flipRefusal(storedRun);
  if (refused) return { refused };
  const sides = freeLegCookSides(hydratedRun, opts);
  const flippedLen = new Map();
  const segments = (storedRun?.segments || []).map((s, i) => {
    if (!s?.freestanding || !sides.has(i)) return s;
    flippedLen.set(i, Math.hypot(s.end.x - s.start.x, s.end.z - s.start.z));
    return { ...s, cookSide: sides.get(i) === 'right' ? 'left' : 'right' };
  });
  // A leg's virtual wall runs the other way once its side flips
  // (`synthesizeVirtualWall` swaps its ends), and a bay's `anchorParam` is
  // measured from that wall's start — so each is re-measured from the other
  // end, or every appliance would slide along its leg (whole-branch review).
  const remeasured = (list) =>
    (list || []).map((b) => {
      const len = flippedLen.get(b?.segmentIndex ?? -1);
      return len != null && Number.isFinite(b.anchorParam)
        ? { ...b, anchorParam: len - b.anchorParam }
        : b;
    });
  return {
    storedRun: {
      ...storedRun,
      segments,
      ...(storedRun.bays ? { bays: remeasured(storedRun.bays) } : {}),
      ...(storedRun.bayOverrides
        ? { bayOverrides: remeasured(storedRun.bayOverrides) }
        : {}),
    },
  };
}

module.exports = {
  FLIP_REFUSAL,
  flipRefusal,
  sideChangeLegs,
  runCabinetSide,
  freeLegCookSides,
  cookSideForNormal,
  flipRunSide,
};
